import { filterFieldShowsAppliedState } from "@/shared/components/Accordion";
import { PROSPECT_DEFAULT_FILTERS, PROSPECT_FILTER_FIELDS } from "../constants/prospectConfig";
import type { ProspectItem } from "../types";

export function getPersonFullName(p: ProspectItem): string {
  if (p.name) return p.name;
  const first = p.first_name || "";
  const last = p.last_name || p.last_name_obfuscated || "";
  return `${first} ${last}`.trim() || "Unnamed contact";
}

export function getPersonCompany(p: ProspectItem): string {
  if (p.organization?.name) return p.organization.name;
  if (p.rawContactOut?.company?.name) return p.rawContactOut.company.name;
  if (p.rawExplorium?.company?.name) return p.rawExplorium.company.name;
  if (p.company) return typeof p.company === "string" ? p.company : p.company.name || "";
  return "Company not specified";
}

export function getPersonLocation(p: ProspectItem): string {
  const parts = [p.city, p.state, p.country].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  if (p.location) return String(p.location);
  if (p.rawContactOut?.country) return p.rawContactOut.country;
  if (p.rawExplorium?.country_name) return p.rawExplorium.country_name;
  return "Location not specified";
}

export function truncateLabel(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

/** One key per LinkedIn profile, whatever the URL spelling (http/https, www., trailing /). */
export function linkedinProfileKey(url: string | undefined): string {
  return String(url || "").trim().toLowerCase().replace(/\/+$/, "").replace(/^https?:\/\//, "").replace(/^www\./, "");
}

/** True when the import call actually added or matched contacts (not a silent failure). */
export function importSucceeded(data: any): boolean {
  const contacts = Array.isArray(data?.contacts) ? data.contacts.length : 0;
  const skipped = Number(data?.summary?.skipped) || (Array.isArray(data?.skipped) ? data.skipped.length : 0);
  return contacts > 0 || skipped > 0;
}

/** How many filters the panel marks as applied, using the panel's own rule, so the
 *  "Filters (n)" button always matches the dots and "n applied" badges inside it. */
export function countActiveFilters(panelFilters: Record<string, any>, appliedFilters: Record<string, any>): number {
  return (PROSPECT_FILTER_FIELDS as Array<{ key: string }>).filter((field) =>
    filterFieldShowsAppliedState(field.key, panelFilters, appliedFilters, PROSPECT_DEFAULT_FILTERS),
  ).length;
}


/** ContactOut's check: "verified" (deliverability confirmed), "unverified" (found, may bounce),
 *  or "" when the status is unknown. */
export function emailQuality(p: ProspectItem): "verified" | "unverified" | "" {
  const status = String(p.availability?.email_status || "").toLowerCase();
  return status === "verified" || status === "unverified" ? status : "";
}
